import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import {
  getReceivedProfileData,
  saveReceivedProfileData,
  getReceivedAnonData,
  saveReceivedAnonData,
  getConnectionMetadata,
  saveConnectionMetadata,
  getNotes,
  saveNotes,
  saveAnonData,
  getConnectionGroup,
  saveConnectionGroup,
  saveLastSelectedGroup,
  getManualContactData,
  saveManualContactData,
  saveChatMessages,
  getChatMessages,
  checkDeletedProfile,
  withoutDeletedMark,
  getLeftConnectionIds,
  saveContactSharingStatus,
  saveContactSavedStatus,
  upsertConnectionView,
  getUserContacts,
  saveUserContacts,
  getNickname,
  saveNickname,
} from '../services/storage/userDataStorage';
import { fillMeetingLocationIfEmpty } from '../services/meetings/meetingRows';
import { fileMeetingFromHostRecord } from '../services/meetings/repeatConnect';
import { MEETING_KIND } from '../services/meetings/meetingRule';
import { setReconnectPin } from '../services/people/reconnectPin';
import { getPeerProfiles } from '../services/people/peerProfiles';
import { loadMessageHistory, loadDataMessages } from '../services/chat/messageStore';
import {
  setGlobalMessageHandler,
  setCatchUpHandler,
  openSessionChannel,
  closeSessionChannel,
} from '../services/chat/chatService';
import { getSenderId, getProfileUserId } from '../utils/identityUtils';
import { mergeLoadedConnection } from '../utils/connectionMerge';
import {
  hasHometownShareFlag,
  keepLiveProfileFields,
  withoutLiveSharedFields,
} from '../utils/connectionName';
import { isLiveSharingReadOn, isNewExperience } from '../utils/newExperience';
import { fromRecordedGuest } from '../services/exchange/roomGuest';
import { mergeIncomingMetadata, recoverPeerRecord } from '../utils/connectionMetadata';
import { useSession } from './SessionContext';

/**
 * ConnectionStateContext - Centralized state management for all connection-related data
 *
 * This context consolidates:
 * - Profile data (shared by profile user)
 * - Anon data (shared by anonymous user)
 * - Connection metadata (location, date, time)
 * - Notes (private notes about connections)
 * - Chat messages
 * - Contact sharing status
 */

const ConnectionStateContext = createContext();

// Storage keys for user preference flags
// Note: HAS_SIGNED_UP_FOR_QR is per-chatRoomId (each Anon user is scoped to a connection)
const USER_PREF_KEYS = {
  HAS_SIGNED_UP_FOR_QR_PREFIX: 'has_signed_up_for_qr_', // + chatRoomId
  WELCOME_BANNER_DISMISSED_PREFIX: 'welcome_banner_dismissed_', // + chatRoomId
  INVITE_BANNER_DISMISSED_PREFIX: 'invite_banner_dismissed_', // + chatRoomId
  WAITLIST_HELD_PREFIX: 'waitlist_held_', // + chatRoomId — joined FROM the expired screen (D10)
};

export const ConnectionStateProvider = ({ children }) => {
  // Master state object - all connection data organized by chatRoomId
  const [connections, setConnections] = useState({});
  const [currentChatRoomId, setCurrentChatRoomId] = useState(null);

  // User preference flags (global, not per-connection)
  const [userPreferences, setUserPreferences] = useState({
    hasSignedUpForQR: false,
    welcomeBannerDismissed: false,
    inviteBannerDismissed: false,
    waitlistHeld: false,
  });

  // Load per-connection preferences when chatRoomId changes
  useEffect(() => {
    if (!currentChatRoomId) {
      // Reset to false when no chatRoomId
      setUserPreferences((prev) => ({
        ...prev,
        hasSignedUpForQR: false,
        welcomeBannerDismissed: false,
        inviteBannerDismissed: false,
        waitlistHeld: false,
      }));
      return;
    }

    const hasSignedUpForQR =
      localStorage.getItem(`${USER_PREF_KEYS.HAS_SIGNED_UP_FOR_QR_PREFIX}${currentChatRoomId}`) ===
      'true';
    const welcomeBannerDismissed =
      localStorage.getItem(
        `${USER_PREF_KEYS.WELCOME_BANNER_DISMISSED_PREFIX}${currentChatRoomId}`
      ) === 'true';
    const inviteBannerDismissed =
      localStorage.getItem(
        `${USER_PREF_KEYS.INVITE_BANNER_DISMISSED_PREFIX}${currentChatRoomId}`
      ) === 'true';
    const waitlistHeld =
      localStorage.getItem(`${USER_PREF_KEYS.WAITLIST_HELD_PREFIX}${currentChatRoomId}`) === 'true';

    setUserPreferences((prev) => ({
      ...prev,
      hasSignedUpForQR,
      welcomeBannerDismissed,
      inviteBannerDismissed,
      waitlistHeld,
    }));

    console.log(
      '[ConnectionStateContext] Loaded per-connection preferences for',
      currentChatRoomId,
      ':',
      { hasSignedUpForQR, welcomeBannerDismissed, inviteBannerDismissed, waitlistHeld }
    );
  }, [currentChatRoomId]);

  // Load all data for a connection from localStorage
  const loadConnectionData = useCallback(async (chatRoomId, isProfileUser) => {
    if (!chatRoomId) return null;

    // Derive isProfileUser from sessionStorage if not explicitly passed
    if (isProfileUser === undefined) {
      const userType = sessionStorage.getItem('user_type');
      isProfileUser = userType === 'profile';
    }

    // Live sharing (D2): the get_shared_items resolver owns every shared detail +
    // hometown, so the recovery composition below must NOT rebuild those from old
    // chat history — a deleted/turned-off detail would survive offline. Under the
    // flag we strip the live-shared fields from history-derived sources (the
    // resolver / offline cache own them); name/photo/peer-gone latches are kept.
    // Gated on the D2 READ switch (off until the backfill), NOT NEW_EXPERIENCE:
    // off → the history rebuild of shared details resumes (pre-D2), so a peer's
    // details survive even when the resolver read is disabled.
    const liveSharing = isLiveSharingReadOn();
    const stripIfLive = (blob) => (liveSharing ? withoutLiveSharedFields(blob) : blob);

    try {
      console.log(
        '[ConnectionStateContext] Loading data for chatRoomId:',
        chatRoomId,
        'isProfileUser:',
        isProfileUser
      );

      // Load all data in parallel
      // IMPORTANT: Use getChatMessages (localStorage) first for instant render,
      // then sync from DB in background to catch missed messages
      let [
        profileData,
        anonData,
        manualContactData,
        metadata,
        noteData,
        chatMessages,
        connectionGroup,
      ] = await Promise.all([
        getReceivedProfileData(chatRoomId),
        getReceivedAnonData(chatRoomId),
        getManualContactData(chatRoomId),
        getConnectionMetadata(chatRoomId),
        getNotes(chatRoomId, isProfileUser),
        getChatMessages(chatRoomId), // Read from localStorage first (fast, no flicker)
        getConnectionGroup(chatRoomId),
      ]);

      // Sync profileData from card-style contact_share messages in chat history
      // Card-style messages carry full profileData for offline recovery —
      // same pattern as Anon's contact_share carrying contacts array
      if (!isProfileUser && chatMessages?.length > 0) {
        const profileCardShares = chatMessages.filter(
          (msg) => msg.type === 'contact_share' && msg.sender === 'profile' && msg.profileData
        );

        if (profileCardShares.length > 0) {
          let mergedProfileData = { ...(profileData || {}) };

          for (const msg of profileCardShares) {
            // Latest message wins — merge full profileData object
            mergedProfileData = { ...mergedProfileData, ...msg.profileData };
          }

          // Only update if we actually got new data
          if (JSON.stringify(mergedProfileData) !== JSON.stringify(profileData || {})) {
            console.log(
              '[ConnectionStateContext] Synced profileData from card-style contact_share:',
              mergedProfileData.sharedFields
            );
            await saveReceivedProfileData(chatRoomId, mergedProfileData);
            profileData = mergedProfileData;
          }
        }
      }

      // Sync anonData from contact_share messages in chat history
      // This mirrors the profileData sync above: merge DB-persisted contact_share
      // data with whatever anonData already exists, so contacts aren't lost when WebSocket misses
      // Filter self-sent: after Anon→Profile conversion, the user's own contact_share
      // messages have sender='anon' but are self-sent (senderId matches after re-keying)
      if (isProfileUser && chatMessages?.length > 0) {
        const mySenderId = getSenderId();
        const anonContactShares = chatMessages.filter(
          (msg) =>
            msg.type === 'contact_share' &&
            msg.sender === 'anon' &&
            msg.contacts?.length > 0 &&
            msg.senderId !== mySenderId
        );

        if (anonContactShares.length > 0) {
          // Start with existing anonData or empty object
          const mergedAnonData = { ...(anonData || {}) };
          const existingSharedFields = new Set(mergedAnonData.sharedFields || []);

          // Extract name from any anon message if not already set
          if (!mergedAnonData.name) {
            const msgWithName = [...chatMessages]
              .reverse()
              .find((msg) => msg.sender === 'anon' && msg.anonName && msg.senderId !== mySenderId);
            if (msgWithName?.anonName) {
              mergedAnonData.name = msgWithName.anonName;
            }
          }

          // Extract contact fields from contact_share messages. Skipped under
          // live sharing (D2): these are live-shared details the resolver owns —
          // rebuilding them from history would resurrect a turned-off/deleted one.
          if (!liveSharing) {
            for (const msg of anonContactShares) {
              for (const contact of msg.contacts) {
                if (contact.type && contact.value) {
                  existingSharedFields.add(contact.type);
                  mergedAnonData[contact.type] = contact.value;
                }
              }
            }
          }

          mergedAnonData.sharedFields = Array.from(existingSharedFields);

          // Set contactType from most recent contact (for Detail page display logic)
          const lastShare = anonContactShares[anonContactShares.length - 1];
          if (lastShare.contacts?.[0]?.type) {
            mergedAnonData.contactType = lastShare.contacts[0].type;
          }

          // Only update if we found new contacts
          if (mergedAnonData.sharedFields.length > (anonData?.sharedFields?.length || 0)) {
            console.log(
              '[ConnectionStateContext] Synced anonData from contact_share messages:',
              mergedAnonData.sharedFields
            );
            await saveReceivedAnonData(chatRoomId, mergedAnonData);
            anonData = mergedAnonData;
          }
        }
      }

      // FIXED: Only check per-connection flag - don't infer from data presence
      // The presence of anonData fields doesn't necessarily mean they were "shared" in this session
      // We rely on the explicit flag set when sharing occurs
      const hasSharedContact = localStorage.getItem(`contact_shared_${chatRoomId}`) === 'true';
      const hasAddedContact = localStorage.getItem(`contact_added_${chatRoomId}`) === 'true';

      // Profile's local name override (separate from anonData.name which is DB-owned)
      const displayName = localStorage.getItem(`display_name_${chatRoomId}`) || null;

      const connectionData = {
        profileData: profileData || null,
        anonData: anonData || null, // FIXED: null instead of {} to detect missing data
        displayName, // Profile's local name override — never touched by DB recovery
        manualContactData: manualContactData || null, // Manual contact data (for manually added contacts)
        connectionMetadata: metadata || null, // FIXED: null instead of {} to detect missing data
        notes: noteData || '',
        connectionGroup: connectionGroup || null, // Legacy
        chatMessages: chatMessages || [],
        hasSharedContactInfo: hasSharedContact,
        hasAddedToContacts: hasAddedContact, // Whether user has saved contact to phone
        lastMetadataUpdate: Date.now(),
      };

      // Merge onto existing context rather than replacing, so a loadConnectionData
      // triggered by navigation/recovery doesn't clobber data just written by
      // updateProfileData / loadUserContacts / loadNickname. Logic + rationale
      // (including the DB-first fields this rebuild must not drop): mergeLoadedConnection.
      setConnections((prev) => ({
        ...prev,
        [chatRoomId]: mergeLoadedConnection(connectionData, prev[chatRoomId] || {}),
      }));

      console.log('[ConnectionStateContext] Data loaded for chatRoomId:', chatRoomId);

      // Background sync: merge DB messages with context.
      // DB is authoritative for persisted messages, but context may contain
      // optimistic adds (via addChatMessage) that haven't hit DB yet.
      // Merge strategy: DB versions win for shared IDs, context-only messages preserved.
      loadMessageHistory(chatRoomId).then((dbMessages) => {
        if (dbMessages && dbMessages.length > 0) {
          setConnections((prev) => {
            if (!prev[chatRoomId]) return prev;
            const contextMessages = prev[chatRoomId].chatMessages || [];

            // Index DB messages by ID for O(1) lookup
            const dbById = new Map(dbMessages.map((m) => [m.id, m]));

            // Keep context-only messages (optimistic adds not yet in DB)
            const optimistic = contextMessages.filter((m) => m.id && !dbById.has(m.id));

            // DB messages first (authoritative order), then optimistic at the end
            const merged = [...dbMessages, ...optimistic];

            return {
              ...prev,
              [chatRoomId]: {
                ...prev[chatRoomId],
                chatMessages: merged,
              },
            };
          });
        }
      });

      // Background: recover data from persisted data messages
      // Runs unconditionally — DB is source of truth for re-shares and all data types.
      // Each sub-block has its own applicability checks so unconditional execution is safe.
      loadDataMessages(chatRoomId).then(async (dataMessages) => {
        if (!dataMessages || dataMessages.length === 0) return;

        const mySenderId = getSenderId();

        // Anon side: recover profileData and connectionMetadata
        if (!isProfileUser) {
          const myProfileUserId = getProfileUserId();
          const profileResponses = dataMessages.filter(
            (msg) =>
              msg.type === 'profile_data_response' &&
              // PXP filter: skip own sends (both sides send on the same chatRoomId)
              (!myProfileUserId || msg.senderProfileUserId !== myProfileUserId)
          );

          if (profileResponses.length > 0) {
            const latest = profileResponses[profileResponses.length - 1];

            // Apply profileData unconditionally — DB is source of truth.
            // Anon has no local edits to profileData, so overwriting is always safe.
            // (Profile's local name edits live in displayName, a separate field.)
            if (latest.profileData) {
              console.log(
                '[ConnectionStateContext] Applying profileData from DB:',
                latest.profileData.sharedFields
              );
              saveReceivedProfileData(chatRoomId, latest.profileData);
              setConnections((prev) => {
                if (!prev[chatRoomId]) return prev;
                return {
                  ...prev,
                  [chatRoomId]: { ...prev[chatRoomId], profileData: latest.profileData },
                };
              });
            }
          }

          // The host's record, rebuilt from its stored messages: the card reply's record
          // (stub fields preserved, enrichment added), then the latest location update
          // (venue edits and a re-meet's new meeting) — no guard, edits always win, but
          // MERGED (mergeIncomingMetadata): replacing dropped what the sender's record
          // didn't carry. One save, and one meeting filing against the FINAL record.
          const latestUpdate = dataMessages
            .filter((msg) => msg.type === 'metadata_updated' && msg.connectionMetadata)
            .pop();
          const recoveredMetadata = recoverPeerRecord(
            metadata,
            profileResponses[profileResponses.length - 1]?.connectionMetadata || null,
            latestUpdate?.connectionMetadata || null
          );
          if (recoveredMetadata) {
            console.log('[ConnectionStateContext] Recovered metadata from the host’s messages');
            saveConnectionMetadata(chatRoomId, recoveredMetadata);
            setConnections((prev) => {
              if (!prev[chatRoomId]) return prev;
              return {
                ...prev,
                [chatRoomId]: { ...prev[chatRoomId], connectionMetadata: recoveredMetadata },
              };
            });
            // A meeting this device has no row for (fresh device, missed live message)
            fileMeetingFromHostRecord(chatRoomId, mySenderId, metadata, recoveredMetadata);
          }
        }

        // Recover anonData from contact messages — merge with existing.
        // ── Peer record recovery: ONE composed write ─────────────────────────
        // Contact message, profile_exchange (PXP), and profile_data_response each
        // used to fire their own save; the saves' DB mirrors are fire-and-forget,
        // so the stored record was a coin flip between sources (list showed the
        // conversion announcement's full name while detail showed the re-shared
        // first name — record-integrity, 2026-07-06). Compose all sources in
        // fixed order (oldest semantics → newest intent) and save ONCE.
        let recovered = anonData ? { ...anonData } : null;

        const contactMessages = dataMessages.filter(
          (msg) => msg.type === 'contact' && msg.anonData?.name && msg.senderId !== mySenderId
        );
        if (contactMessages.length > 0) {
          const latest = contactMessages[contactMessages.length - 1];
          // Preserve existing name — DB contact message carries the connection-time
          // name, but profile lookup may have resolved the current name from profiles table.
          const priorName = recovered?.name;
          recovered = {
            ...(recovered || {}),
            ...stripIfLive(latest.anonData),
          };
          if (priorName) {
            recovered.name = priorName;
          }
        }

        // PXP Guest data from profile_exchange messages. Stamped reconnection
        // envelopes are routing artifacts — recomposing them materializes the
        // envelope room as a ghost peer record (all real data lives in the old room).
        const pxpMessages = dataMessages.filter(
          (msg) =>
            msg.type === 'profile_exchange' && msg.senderId !== mySenderId && !msg.isReconnection
        );
        if (pxpMessages.length > 0) {
          const latest = pxpMessages[pxpMessages.length - 1];
          recovered = {
            ...(recovered || {}),
            ...stripIfLive(latest.profileData),
            senderProfileUserId: latest.senderProfileUserId,
          };
        }

        // Profile side: recover peer-sent metadata (venue/location). This
        // previously ran only for anon viewers — a user who converted lost the
        // venue permanently even though the room's persisted messages carry it
        // (record-integrity, 2026-07-05). Layering: the exchange-time
        // profile_data_response snapshot first, then any metadata_updated
        // (explicit venue edit) over it; both peer-sent only, merged over own
        // stub fields.
        if (isProfileUser) {
          const peerMetaOf = (type) => {
            const msgs = dataMessages.filter(
              (msg) =>
                msg.type === type &&
                msg.connectionMetadata &&
                msg.senderId !== mySenderId &&
                !msg.isReconnection
            );
            return msgs.length > 0 ? msgs[msgs.length - 1].connectionMetadata : null;
          };
          const responseMeta = peerMetaOf('profile_data_response');
          const updatedMeta = peerMetaOf('metadata_updated');
          // PXP rooms skip peer-metadata recovery entirely — mirrors the live
          // handler's rule ("PXP users manage their own location"). Once a room
          // is profile-to-profile (formed PXP, or upgraded by a re-meet merge)
          // the peer's exchange-time snapshot must not overwrite local meeting
          // data: re-applying the original pdr on every open silently reverted
          // a re-meet's new meetingDate/venue (defect 3, DB-proven 2026-07-18).
          // Anon↔Profile rooms keep the layer — converted users recover the
          // venue from it. Message-level check covers a fresh device where
          // local metadata hasn't hydrated yet.
          const isPXPRoom =
            metadata?.exchangeType === 'pxp' ||
            responseMeta?.exchangeType === 'pxp' ||
            updatedMeta?.exchangeType === 'pxp';
          const mergedMeta =
            !isPXPRoom && (responseMeta || updatedMeta)
              ? { ...metadata, ...(responseMeta || {}), ...(updatedMeta || {}) }
              : null;
          // Changed-guard: this runs on every connection open / app foreground —
          // skip the localStorage write + Supabase upsert (and the venue-backfill
          // SELECT) when recovery found nothing new. Accepted edge: a venue-less
          // meeting beside already-stable metadata fills on the next metadata
          // change or via the live handler, not on idle re-opens.
          if (mergedMeta && JSON.stringify(mergedMeta) !== JSON.stringify(metadata)) {
            saveConnectionMetadata(chatRoomId, mergedMeta);
            setConnections((prev) => {
              if (!prev[chatRoomId]) return prev;
              return {
                ...prev,
                [chatRoomId]: { ...prev[chatRoomId], connectionMetadata: mergedMeta },
              };
            });
            // Fill our own meeting row's empty place (gated inside: an
            // unconfirmed host venue fills only the area — device pass 2)
            fillMeetingLocationIfEmpty(chatRoomId, mySenderId, mergedMeta);
          }
        }

        // Profile side: peer's profile_data_response — the peer's LATEST sharing
        // intent (name per their nameSharing choice, re-shared fields). Applied
        // last so it wins over the connection-time contact/announcement data.
        if (isProfileUser) {
          const myProfileUserId = getProfileUserId();
          const peerProfileResponses = dataMessages.filter(
            (msg) =>
              msg.type === 'profile_data_response' &&
              msg.senderProfileUserId &&
              msg.senderProfileUserId !== myProfileUserId &&
              !msg.isReconnection
          );
          if (peerProfileResponses.length > 0) {
            const latest = peerProfileResponses[peerProfileResponses.length - 1];
            if (latest.profileData) {
              recovered = {
                ...(recovered || {}),
                ...stripIfLive(latest.profileData),
                senderProfileUserId: latest.senderProfileUserId,
              };
            }
          }
        }

        // The live profile owns colour + photo: history only fills a gap
        recovered = keepLiveProfileFields(recovered, anonData);

        // The single authoritative write for everything recovered above
        if (recovered && JSON.stringify(recovered) !== JSON.stringify(anonData)) {
          const recoveredFinal = recovered;
          await saveReceivedAnonData(chatRoomId, recoveredFinal);
          setConnections((prev) => {
            if (!prev[chatRoomId]) return prev;
            return {
              ...prev,
              [chatRoomId]: { ...prev[chatRoomId], anonData: recoveredFinal },
            };
          });
        }
      });

      // Background: check if peer has upgraded to Profile (fetch photo, name, senderProfileUserId)
      // Runs when anonData is missing photo OR senderProfileUserId (conversion detection)
      if (
        isProfileUser &&
        (!anonData?.photoUrl || !anonData?.senderProfileUserId) &&
        chatMessages?.length > 0
      ) {
        const myId = getSenderId();
        const peerMessage = chatMessages.find((msg) => msg.senderId && msg.senderId !== myId);
        if (peerMessage?.senderId) {
          // Checked peer read (open-access lockdown): name, photo and city
          // only — never the peer's raw contacts.
          getPeerProfiles([peerMessage.senderId]).then(({ rows }) => {
            const peer = rows.get(peerMessage.senderId);
            if (peer?.profileExists) {
              // Merge profile data on top of existing anonData (preserves contacts shared during exchange)
              const peerHometown = peer.currentCity;
              const merged = {
                ...(anonData || {}),
                senderProfileUserId: peerMessage.senderId,
                ...(peer.photoUrl && { photoUrl: peer.photoUrl }),
                // Fill-missing only: the profile holds the RAW full name and
                // doesn't know the peer's nameSharing choice — the shared name in
                // anonData is authoritative (same rule as the list enrichment).
                ...(peer.name && !anonData?.name && { name: peer.name }),
                // Hometown is value-refresh ONLY, and only for a record already
                // flagged as shared ('currentCity' or legacy 'aboutMe'): the flag
                // is never added here — consent comes from an explicit share (or
                // the Conversion Grant), never from a refresh path. Suppressed
                // under live sharing — the resolver owns the 'livesIn' item.
                ...(!liveSharing &&
                  peerHometown &&
                  hasHometownShareFlag(anonData) && {
                    currentCity: peerHometown,
                    // where the hometown is, when they picked it (051)
                    ...(peer.currentCityPlace && { currentCityPlace: peer.currentCityPlace }),
                  }),
              };
              saveReceivedAnonData(chatRoomId, merged);
              setConnections((prev) => {
                if (!prev[chatRoomId]) return prev;
                return {
                  ...prev,
                  [chatRoomId]: {
                    ...prev[chatRoomId],
                    anonData: merged,
                  },
                };
              });
              console.log(
                '[ConnectionStateContext] Merged peer profile into anonData:',
                peerMessage.senderId
              );
            }
          });
        }
      }

      // Check if peer's profile has been deleted (separate from conversion detection above)
      if (isProfileUser && anonData?.senderProfileUserId && !anonData?.peerProfileDeleted) {
        checkDeletedProfile(anonData.senderProfileUserId).then((isDeleted) => {
          if (isDeleted === true) {
            const merged = { ...anonData, peerProfileDeleted: true };
            saveReceivedAnonData(chatRoomId, merged);
            setConnections((prev) => {
              if (!prev[chatRoomId]) return prev;
              return {
                ...prev,
                [chatRoomId]: { ...prev[chatRoomId], anonData: merged },
              };
            });
            console.log(
              '[ConnectionStateContext] Peer profile deleted:',
              anonData.senderProfileUserId
            );
          }
        });
      }

      // A deleted mark the server says was wrong (the person still exists) is
      // cleared — the lockdown's closed profiles read once made every peer
      // look deleted to the previous app version (one-way latch, now healable)
      if (isProfileUser && anonData?.senderProfileUserId && anonData?.peerProfileDeleted) {
        checkDeletedProfile(anonData.senderProfileUserId).then((isDeleted) => {
          if (isDeleted !== false) return;
          const healed = withoutDeletedMark(anonData);
          saveReceivedAnonData(chatRoomId, healed);
          setConnections((prev) => {
            if (!prev[chatRoomId]) return prev;
            return { ...prev, [chatRoomId]: { ...prev[chatRoomId], anonData: healed } };
          });
        });
      }

      // Check if the peer has LEFT this connection (per-side delete, CD #15) —
      // parallel to the deleted-profile check above; latch peerLeft so my copy
      // freezes + becomes editable (isPeerGone). They are not told.
      if (
        isProfileUser &&
        anonData?.senderProfileUserId &&
        !anonData?.peerLeft &&
        !anonData?.peerProfileDeleted
      ) {
        getLeftConnectionIds([{ chatRoomId, anonData }], getProfileUserId()).then((leftIds) => {
          if (leftIds.length > 0) {
            const merged = { ...anonData, peerLeft: true };
            saveReceivedAnonData(chatRoomId, merged);
            setConnections((prev) => {
              if (!prev[chatRoomId]) return prev;
              return {
                ...prev,
                [chatRoomId]: { ...prev[chatRoomId], anonData: merged },
              };
            });
            console.log('[ConnectionStateContext] Peer left connection:', chatRoomId);
          }
        });
      }

      // Return the loaded data so it can be used immediately
      return connectionData;
    } catch (error) {
      console.error('[ConnectionStateContext] Error loading connection data:', error);
      // Initialize empty connection if loading fails
      const emptyConnection = {
        profileData: null,
        anonData: null, // FIXED: null instead of {} to indicate no data
        manualContactData: null, // Manual contact data (for manually added contacts)
        connectionMetadata: null, // FIXED: null instead of {} to indicate no data
        notes: '',
        connectionGroup: null, // Legacy
        chatMessages: [],
        hasSharedContactInfo: false,
        hasAddedToContacts: false, // Whether user has saved contact to phone
        linkRequest: null, // Anon user's request for Profile to text them the link
        lastMetadataUpdate: Date.now(),
      };

      setConnections((prev) => ({
        ...prev,
        [chatRoomId]: emptyConnection,
      }));

      return emptyConnection;
    }
  }, []); // Removed connections from dependency array - we use functional setState

  // Update profile data (from profile user)
  const updateProfileData = useCallback(async (chatRoomId, profileData) => {
    if (!chatRoomId) return;

    console.log('[ConnectionStateContext] Updating profile data for:', chatRoomId);

    // Save to localStorage
    await saveReceivedProfileData(chatRoomId, profileData);

    // IMPORTANT: Read hasSharedContactInfo from localStorage to preserve it
    // This prevents losing the flag if prev[chatRoomId] is undefined
    const hasSharedContactInfo = localStorage.getItem(`contact_shared_${chatRoomId}`) === 'true';

    // Update context state
    setConnections((prev) => ({
      ...prev,
      [chatRoomId]: {
        ...prev[chatRoomId],
        profileData,
        hasSharedContactInfo: prev[chatRoomId]?.hasSharedContactInfo || hasSharedContactInfo,
        lastMetadataUpdate: Date.now(),
      },
    }));
  }, []);

  // Update anon data (from anonymous user)
  // Profile's local name override — separate from anonData (DB-owned)
  const updateDisplayName = useCallback((chatRoomId, name) => {
    if (!chatRoomId) return;
    const newName = name || null;
    if (name) {
      localStorage.setItem(`display_name_${chatRoomId}`, name);
    } else {
      localStorage.removeItem(`display_name_${chatRoomId}`);
    }
    // Dual-write to connection_views
    const userId = getProfileUserId();
    if (userId) {
      upsertConnectionView(chatRoomId, userId, { displayName: newName });
    }
    setConnections((prev) => {
      if (!prev[chatRoomId]) return prev;
      if (prev[chatRoomId].displayName === newName) return prev; // no-op guard
      return {
        ...prev,
        [chatRoomId]: { ...prev[chatRoomId], displayName: newName },
      };
    });
  }, []);

  const updateAnonData = useCallback(async (chatRoomId, anonData) => {
    if (!chatRoomId) return;

    console.log('[ConnectionStateContext] Updating anon data for:', chatRoomId);

    // Save to localStorage based on user type
    if (anonData.isOwnData) {
      // Anonymous user saving their own data
      await saveAnonData(anonData, chatRoomId);
    } else {
      // Profile user receiving anon data
      await saveReceivedAnonData(chatRoomId, anonData);
    }

    // Update context state
    setConnections((prev) => ({
      ...prev,
      [chatRoomId]: {
        ...prev[chatRoomId],
        anonData,
        lastMetadataUpdate: Date.now(),
      },
    }));
  }, []);

  // Update manual contact data (for manually added contacts)
  const updateManualContactData = useCallback(async (chatRoomId, manualContactData) => {
    if (!chatRoomId) return;

    console.log('[ConnectionStateContext] Updating manual contact data for:', chatRoomId);

    // Save first; surface a failure so the shared contacts sheet shows its line
    // (the sheet edits both record types and consumes { error } the same way).
    // Note: for a manual contact this is the LOCAL-first result —
    // saveManualContactData mirrors to Supabase fire-and-forget by design — so
    // it's a weaker guarantee than updateUserContacts' awaited DB write.
    const ok = await saveManualContactData(chatRoomId, manualContactData);
    if (!ok) return { error: 'save_failed' };

    // Update context state
    setConnections((prev) => ({
      ...prev,
      [chatRoomId]: {
        ...prev[chatRoomId],
        manualContactData,
        lastMetadataUpdate: Date.now(),
      },
    }));
  }, []);

  // Update connection metadata
  const updateConnectionMetadata = useCallback(async (chatRoomId, metadata) => {
    if (!chatRoomId) return;

    console.log('[ConnectionStateContext] Updating connection metadata for:', chatRoomId);

    // Save to localStorage
    await saveConnectionMetadata(chatRoomId, metadata);

    // IMPORTANT: Read hasSharedContactInfo from localStorage to preserve it
    const hasSharedContactInfo = localStorage.getItem(`contact_shared_${chatRoomId}`) === 'true';

    // Update context state
    setConnections((prev) => ({
      ...prev,
      [chatRoomId]: {
        ...prev[chatRoomId],
        connectionMetadata: metadata,
        hasSharedContactInfo: prev[chatRoomId]?.hasSharedContactInfo || hasSharedContactInfo,
        lastMetadataUpdate: Date.now(),
      },
    }));
  }, []);

  // Add a single chat message to state (with dedup).
  // Declared here (before the global handler) to avoid Temporal Dead Zone —
  // the useEffect deps array is evaluated during render, so addChatMessage
  // must be declared before it's referenced.
  const addChatMessage = useCallback((chatRoomId, message) => {
    if (!chatRoomId) return;

    // Update React state and sync to localStorage for list views
    // DB persistence happens in chatService.sendMessage() via persistMessage()
    setConnections((prev) => {
      const existing = prev[chatRoomId]?.chatMessages || [];
      // Dedup: both global handler and component handler fire for the same message.
      // DB-sourced messages always have an id (from message_id). Skip if already present.
      // Optimistic messages without real IDs (msg_${Date.now()}) pass through because
      // message.id && ... short-circuits.
      if (message.id) {
        const match = existing.find((m) => m.id === message.id);
        if (match) {
          // Same ID, different content → content update (system message location/venue change,
          // or photo upload blob URL → Supabase URL swap). Merge all fields from incoming message.
          if (match.content !== message.content) {
            const updatedMessages = existing.map((m) =>
              m.id === message.id ? { ...m, ...message } : m
            );
            saveChatMessages(chatRoomId, updatedMessages);
            return {
              ...prev,
              [chatRoomId]: { ...prev[chatRoomId], chatMessages: updatedMessages },
            };
          }
          // Same ID, same content → true duplicate, skip.
          return prev;
        }
      }

      const updatedMessages = [...existing, message];

      // Sync to localStorage so connectionStorage.getConnections() can read for list views
      saveChatMessages(chatRoomId, updatedMessages);

      return {
        ...prev,
        [chatRoomId]: {
          ...prev[chatRoomId],
          chatMessages: updatedMessages,
        },
      };
    });
  }, []);

  // Global WebSocket message handler — persists critical data types regardless of which
  // screen is mounted. Component handlers continue to own UI, analytics, and navigation.
  // NOTE: Must be declared AFTER updateProfileData, updateAnonData, updateConnectionMetadata,
  // and addChatMessage to avoid Temporal Dead Zone errors (const declarations are not hoisted).
  useEffect(() => {
    const handler = async (payload) => {
      const { type, chatRoomId } = payload;
      if (!chatRoomId) return;

      if (type === 'profile_data_response') {
        // Skip self-sent messages (PXP echo filtering)
        const myProfileUserId = localStorage.getItem('profile_user_id');
        if (payload.senderProfileUserId && payload.senderProfileUserId === myProfileUserId) {
          return;
        }

        // Reconnection handshake envelope — all real data converges on the OLD
        // room via the component handlers' merge; materializing a peer record
        // under the envelope room id creates the ghost duplicate chat.
        if (payload.isReconnection) {
          return;
        }

        if (payload.profileData) {
          const processedProfileData = {
            ...payload.profileData,
            sharedFields: payload.profileData.sharedFields || ['name', 'photoUrl'],
          };

          // PXP-aware routing: if I'M a Profile user AND the sender is a Profile user,
          // this is PXP — store in anonData slot (matches ProfileConnectionPage behavior).
          // senderProfileUserId is present in BOTH flows, so we also check if the
          // receiver is a Profile user (myProfileUserId) to distinguish PXP from Anon↔Profile.
          const isPXPData = myProfileUserId && payload.senderProfileUserId;
          if (isPXPData) {
            // Carry the sender's identity into the record — updateAnonData replaces,
            // and losing senderProfileUserId would demote the peer to "anon"
            // (editable name, wrong precedence) until the next recovery.
            updateAnonData(chatRoomId, {
              ...processedProfileData,
              senderProfileUserId: payload.senderProfileUserId,
            });
          } else {
            updateProfileData(chatRoomId, processedProfileData);
          }
        }

        // Save metadata (skip for PXP — PXP users manage their own location)
        // Note: senderProfileUserId is present in BOTH Anon↔Profile and PXP flows,
        // so we check exchangeType on the metadata to distinguish PXP
        const isPXP = payload.connectionMetadata?.exchangeType === 'pxp';
        if (payload.connectionMetadata && !isPXP) {
          const processedMetadata = { ...payload.connectionMetadata };

          // Normalize meetingDate to ISO string
          if (processedMetadata.meetingDate) {
            if (typeof processedMetadata.meetingDate === 'object') {
              processedMetadata.meetingDate = processedMetadata.meetingDate.toISOString();
            } else if (typeof processedMetadata.meetingDate === 'number') {
              processedMetadata.meetingDate = new Date(processedMetadata.meetingDate).toISOString();
            }
          }

          processedMetadata.lastUpdated = new Date().toISOString();
          // The guest files its meeting row under the host's key (the host decides)
          fileMeetingFromHostRecord(
            chatRoomId,
            getSenderId(),
            await getConnectionMetadata(chatRoomId),
            processedMetadata
          );
          updateConnectionMetadata(chatRoomId, processedMetadata);
        }
      }

      // New design: a guest's name and contact count only from the room's
      // recorded guest — never from a third phone that wrote into the room
      // while its code was live (roomGuest.js; Mark, 2026-10-21). The old
      // design never awaits it (the flag check comes first, synchronously).
      const fromGuest = async () => {
        if (await fromRecordedGuest(chatRoomId, payload.senderId)) return true;
        console.warn(
          '[GlobalHandler] guest data from a phone that is not the room’s guest — ignored',
          {
            chatRoomId,
            senderId: payload.senderId,
          }
        );
        return false;
      };

      if (
        type === 'contact' &&
        payload.sender === 'anon' &&
        (!isNewExperience() || (await fromGuest()))
      ) {
        if (payload.anonData) {
          updateAnonData(chatRoomId, payload.anonData);
          // Anon owns the latest name — clear Profile's local override so it doesn't block
          if (payload.anonData.name) {
            updateDisplayName(chatRoomId, null);
          }
        }
        if (payload.connectionMetadata) {
          updateConnectionMetadata(chatRoomId, payload.connectionMetadata);
        }
      }

      // Conversion announcement (name/photo/hometown + re-declared shares).
      // Merge-preserve: authoritative for what it carries, but must not wipe
      // fields it doesn't (senderProfileUserId, earlier chat extractions) —
      // updateAnonData replaces, never merges (deterministic-ID race gotcha).
      if (type === 'contact' && payload.sender === 'upgraded_user' && payload.anonData) {
        try {
          const freshAnonData = (await getReceivedAnonData(chatRoomId)) || {};
          const mergedContact = {
            ...freshAnonData,
            ...payload.anonData,
            sharedFields: [
              ...new Set([
                ...(freshAnonData.sharedFields || []),
                ...(payload.anonData.sharedFields || []),
              ]),
            ],
          };
          updateAnonData(chatRoomId, mergedContact);
          if (payload.anonData.name) {
            updateDisplayName(chatRoomId, null); // peer owns their name now
          }
        } catch (err) {
          console.error('[ConnectionStateContext] upgraded_user contact merge failed:', err);
        }
      }

      if (type === 'metadata_updated') {
        if (payload.connectionMetadata) {
          // MERGE onto our record, never replace — the peer's payload is built
          // from THEIR metadata, so anything only we hold (scan-time
          // meetingDate above all) used to vanish on arrival. See
          // mergeIncomingMetadata for the full reasoning + unit tests.
          const existingMetadata = await getConnectionMetadata(chatRoomId);
          // A re-meet reaches the guest this way: file the new meeting under its key
          fileMeetingFromHostRecord(
            chatRoomId,
            getSenderId(),
            existingMetadata,
            payload.connectionMetadata
          );
          const processedMetadata = {
            ...mergeIncomingMetadata(existingMetadata, payload.connectionMetadata),
            lastUpdated: new Date().toISOString(),
          };
          updateConnectionMetadata(chatRoomId, processedMetadata);
          // The host's verdict on a repeat connect (E3b-1 step 10): a reconnect
          // pins them on top of our People. From the verdict only — never
          // decided here; setReconnectPin is flag-gated and needs a profile.
          if (payload.meeting?.kind === MEETING_KIND.RECONNECT) setReconnectPin(chatRoomId);
          // Fill our own meeting's venue if it arrived placeless (fill-once;
          // a manually-added place is never overwritten). Gated at the WRITE:
          // only a confirmed venue may fill (device pass 2 — the host's
          // background location push also rides metadata_updated, unconfirmed).
          fillMeetingLocationIfEmpty(chatRoomId, getSenderId(), processedMetadata);
        }
      }

      // Persist chat/system/contact_share messages via addChatMessage.
      // When component handler is null (navigated away), this is the only path.
      // When both fire, dedup in addChatMessage (3a) prevents duplicates.
      if (type === 'chat' || type === 'system') {
        addChatMessage(chatRoomId, payload);
        // Flag unread chats for BottomTabBar when a chat message arrives from someone else
        if (type === 'chat' && payload.senderId !== getSenderId()) {
          localStorage.setItem('has_unread_chats', 'true');
        }
      }

      // Extract anonName/contactType embedded in chat messages from Anon users.
      // Single read + single write to avoid self-race when a message carries both fields.
      if (
        type === 'chat' &&
        payload.sender === 'anon' &&
        (payload.anonName || payload.contactType) &&
        (!isNewExperience() || (await fromGuest()))
      ) {
        try {
          const freshAnonData = (await getReceivedAnonData(chatRoomId)) || {};
          const updates = { ...freshAnonData };
          let changed = false;

          if (payload.anonName && freshAnonData.name !== payload.anonName) {
            updates.name = payload.anonName;
            changed = true;
            // Anon owns the latest name — clear Profile's local override
            updateDisplayName(chatRoomId, null);
          }

          if (payload.contactType) {
            updates[payload.contactType] = payload.content;
            updates.sharedFields = [
              ...new Set([...(freshAnonData.sharedFields || []), payload.contactType]),
            ];
            changed = true;
          }

          if (changed) {
            updateAnonData(chatRoomId, updates);
          }
        } catch (err) {
          console.error('[GlobalHandler] anonName/contactType extraction failed:', err);
        }
      }

      if (type === 'contact_share') {
        addChatMessage(chatRoomId, payload);
        // Only mark sharing status for self-sent contact_share messages.
        // Receiving the peer's contact card should NOT flip hasSharedContactInfo,
        // which controls whether the Anon input shows phone entry vs chat.
        if (payload.senderId && payload.senderId === getSenderId()) {
          saveContactSharingStatus(chatRoomId, true);
        }
      }
    };

    setGlobalMessageHandler(handler);
    return () => setGlobalMessageHandler(null);
  }, [
    updateProfileData,
    updateAnonData,
    updateConnectionMetadata,
    updateDisplayName,
    addChatMessage,
  ]);

  // Catch-up after a gap (chat-reconnect-catchup): the chat service signals a
  // room may have missed inserts (feed rejoined, back online, back in front);
  // re-read it the way a page load does — DB history merged by id (no
  // duplicates), data messages replayed (re-shared cards, venue edits).
  useEffect(() => {
    setCatchUpHandler((chatRoomId) => loadConnectionData(chatRoomId));
    return () => setCatchUpHandler(null);
  }, [loadConnectionData]);

  // Persistent session channel — ensure the channel stays open for the entire app session.
  // When sessionChatRoomId is set, open (or mark) the channel. When it changes, close the old one.
  const { sessionChatRoomId } = useSession();

  useEffect(() => {
    if (!sessionChatRoomId) return;
    openSessionChannel(sessionChatRoomId);
    return () => closeSessionChannel(sessionChatRoomId);
  }, [sessionChatRoomId]);

  // Update notes
  const updateNotes = useCallback(async (chatRoomId, notes, isProfileUser = false) => {
    if (!chatRoomId) return;

    console.log('[ConnectionStateContext] Updating notes for:', chatRoomId);

    // Save to localStorage
    await saveNotes(chatRoomId, notes, isProfileUser);

    // Update context state
    setConnections((prev) => ({
      ...prev,
      [chatRoomId]: {
        ...prev[chatRoomId],
        notes,
        lastMetadataUpdate: Date.now(),
      },
    }));
  }, []);

  // Update chat messages — accepts either an array (direct replace) or an updater
  // function (prev => next). The updater pattern runs inside setConnections so it
  // always reads the latest state — eliminates stale ref issues in callers.
  const updateChatMessages = useCallback((chatRoomId, messagesOrUpdater) => {
    if (!chatRoomId) return;

    const hasSharedContactInfo = localStorage.getItem(`contact_shared_${chatRoomId}`) === 'true';

    setConnections((prev) => {
      const existing = prev[chatRoomId]?.chatMessages || [];
      const newMessages =
        typeof messagesOrUpdater === 'function' ? messagesOrUpdater(existing) : messagesOrUpdater;

      // Sync to localStorage so connectionStorage.getConnections() can read for list views
      saveChatMessages(chatRoomId, newMessages);

      return {
        ...prev,
        [chatRoomId]: {
          ...prev[chatRoomId],
          chatMessages: newMessages,
          hasSharedContactInfo: prev[chatRoomId]?.hasSharedContactInfo || hasSharedContactInfo,
        },
      };
    });
    // Note: DB persistence happens in chatService.sendMessage() via persistMessage()
  }, []);

  // Update contact sharing status
  const updateContactSharingStatus = useCallback(async (chatRoomId, hasShared) => {
    if (!chatRoomId) return;

    console.log('[ConnectionStateContext] Updating contact sharing status for:', chatRoomId);

    // Save to localStorage + dual-write to Supabase via storage function
    await saveContactSharingStatus(chatRoomId, hasShared);

    // Update context state
    setConnections((prev) => ({
      ...prev,
      [chatRoomId]: {
        ...prev[chatRoomId],
        hasSharedContactInfo: hasShared,
      },
    }));
  }, []);

  // Update contact saved to phone status (for hiding "Add to contacts" button after saving)
  const updateContactSavedStatus = useCallback(async (chatRoomId, hasSaved) => {
    if (!chatRoomId) return;

    console.log('[ConnectionStateContext] Updating contact saved status for:', chatRoomId);

    // Save to localStorage + dual-write to Supabase via storage function
    await saveContactSavedStatus(chatRoomId, hasSaved);

    // Update context state
    setConnections((prev) => ({
      ...prev,
      [chatRoomId]: {
        ...prev[chatRoomId],
        hasAddedToContacts: hasSaved,
      },
    }));
  }, []);

  // Update connection group (legacy)
  const updateConnectionGroup = useCallback(async (chatRoomId, group) => {
    if (!chatRoomId) return;

    console.log(
      '[ConnectionStateContext] Updating connection group for:',
      chatRoomId,
      'to:',
      group
    );

    // Save to localStorage
    await saveConnectionGroup(chatRoomId, group);
    // Also update global default for next new connection
    await saveLastSelectedGroup(group);

    // Update context state
    setConnections((prev) => ({
      ...prev,
      [chatRoomId]: {
        ...prev[chatRoomId],
        connectionGroup: group,
        lastMetadataUpdate: Date.now(),
      },
    }));
  }, []);

  // Clear all connection data for a specific chatRoomId
  // Used when P2P discovery occurs to start fresh connection
  // IMPORTANT: Preserves connectionMetadata (location/date/time) to avoid losing location data
  const clearConnectionData = useCallback(async (chatRoomId) => {
    if (!chatRoomId) return;

    console.log('[ConnectionStateContext] Clearing connection data for:', chatRoomId);

    // CRITICAL: Save location metadata BEFORE clearing
    const existingMetadata = await getConnectionMetadata(chatRoomId);
    console.log('[ConnectionStateContext] Preserving metadata during clear:', existingMetadata);

    // FIXED: Clear context state FIRST to prevent race conditions
    setConnections((prev) => {
      const updated = { ...prev };
      delete updated[chatRoomId];
      return updated;
    });

    // Then remove all localStorage keys for this chatRoomId
    // NOTE: We remove connection_metadata here but will restore it below
    await Promise.all([
      Promise.resolve(localStorage.removeItem(`received_profile_data_${chatRoomId}`)),
      Promise.resolve(localStorage.removeItem(`received_anon_data_${chatRoomId}`)),
      Promise.resolve(localStorage.removeItem(`anon_user_data_${chatRoomId}`)),
      Promise.resolve(localStorage.removeItem(`contact_shared_${chatRoomId}`)),
      Promise.resolve(localStorage.removeItem(`connection_metadata_${chatRoomId}`)),
      Promise.resolve(localStorage.removeItem(`profile_notes_${chatRoomId}`)),
      Promise.resolve(localStorage.removeItem(`anon_notes_${chatRoomId}`)),
      Promise.resolve(localStorage.removeItem(`chat_messages_${chatRoomId}`)),
      Promise.resolve(localStorage.removeItem(`connection_group_${chatRoomId}`)),
    ]);

    // CRITICAL: Restore location metadata if it existed
    if (existingMetadata && existingMetadata.location) {
      await saveConnectionMetadata(chatRoomId, existingMetadata);
      console.log(
        '[ConnectionStateContext] Restored location metadata after clear:',
        existingMetadata
      );
    }

    console.log('[ConnectionStateContext] Connection data cleared for:', chatRoomId);
  }, []);

  // Get connection data for a specific chat room - PURE function, no side effects
  const getConnectionData = useCallback(
    (chatRoomId) => {
      if (!chatRoomId) return null;
      return connections[chatRoomId] || null;
    },
    [connections]
  );

  // User preference flag methods
  // Takes chatRoomId as first parameter (following pattern of other methods)
  const setHasSignedUpForQR = useCallback(
    (chatRoomId, value = true) => {
      if (!chatRoomId) {
        console.warn('[ConnectionStateContext] Cannot set hasSignedUpForQR without chatRoomId');
        return;
      }
      console.log('[ConnectionStateContext] Setting hasSignedUpForQR for', chatRoomId, ':', value);
      localStorage.setItem(
        `${USER_PREF_KEYS.HAS_SIGNED_UP_FOR_QR_PREFIX}${chatRoomId}`,
        value ? 'true' : 'false'
      );
      // Update state if this is for the current chatRoomId
      if (chatRoomId === currentChatRoomId) {
        setUserPreferences((prev) => ({
          ...prev,
          hasSignedUpForQR: value,
        }));
      }
    },
    [currentChatRoomId]
  );

  // Joined FROM the expired screen (exchange-clarity D10): the room's view
  // becomes the held screen ("Your chat is waiting for you") instead of a
  // silently-reopened dead chat — the durable keep is the server-side
  // waitlist tie + conversion restore. Pre-expiry joiners never set this;
  // their never-expires live chat is untouched.
  const setWaitlistHeld = useCallback(
    (chatRoomId, value = true) => {
      if (!chatRoomId) {
        console.warn('[ConnectionStateContext] Cannot set waitlistHeld without chatRoomId');
        return;
      }
      localStorage.setItem(
        `${USER_PREF_KEYS.WAITLIST_HELD_PREFIX}${chatRoomId}`,
        value ? 'true' : 'false'
      );
      if (chatRoomId === currentChatRoomId) {
        setUserPreferences((prev) => ({
          ...prev,
          waitlistHeld: value,
        }));
      }
    },
    [currentChatRoomId]
  );

  // Permanently dismiss the docked welcome/invite banner for a connection (small text X).
  // `which` = 'welcome' | 'invite' — separate flags so declining the waitlist doesn't also
  // suppress a later real-access invite.
  const dismissBanner = useCallback(
    (chatRoomId, which) => {
      if (!chatRoomId || (which !== 'welcome' && which !== 'invite')) {
        console.warn('[ConnectionStateContext] dismissBanner needs chatRoomId + welcome|invite');
        return;
      }
      const prefix =
        which === 'welcome'
          ? USER_PREF_KEYS.WELCOME_BANNER_DISMISSED_PREFIX
          : USER_PREF_KEYS.INVITE_BANNER_DISMISSED_PREFIX;
      localStorage.setItem(`${prefix}${chatRoomId}`, 'true');
      if (chatRoomId === currentChatRoomId) {
        setUserPreferences((prev) => ({
          ...prev,
          [which === 'welcome' ? 'welcomeBannerDismissed' : 'inviteBannerDismissed']: true,
        }));
      }
    },
    [currentChatRoomId]
  );

  // User-added contacts (DB-first) — the owner's own contact info for this person,
  // stored on connection_views.user_contacts. This is the first DB-first field in this
  // context: the DB is the source of truth, localStorage is a cache for fast first paint.
  // See data-and-state-guide.md § DB-First Architecture. Independent of the legacy
  // loadConnectionData path — do not interleave the two.
  const loadUserContacts = useCallback(async (chatRoomId) => {
    if (!chatRoomId) return;
    const userId = getSenderId();
    if (!userId) return;

    // Paint the cached value immediately, then refresh from the DB (source of truth).
    const cached = localStorage.getItem(`user_contacts_${chatRoomId}`);
    if (cached) {
      try {
        const parsed = JSON.parse(cached);
        setConnections((prev) => ({
          ...prev,
          [chatRoomId]: { ...prev[chatRoomId], userContacts: parsed },
        }));
      } catch {
        // Corrupted cache — ignore; the DB refresh below is authoritative.
      }
    }

    const contacts = await getUserContacts(chatRoomId, userId);
    localStorage.setItem(`user_contacts_${chatRoomId}`, JSON.stringify(contacts));
    setConnections((prev) => ({
      ...prev,
      [chatRoomId]: { ...prev[chatRoomId], userContacts: contacts },
    }));
  }, []);

  const updateUserContacts = useCallback(async (chatRoomId, contacts) => {
    if (!chatRoomId) return;
    const userId = getSenderId();
    if (!userId) return;

    const next = contacts || {};

    // DB first (awaited). If the write fails, don't update cache/state — never let
    // stale data pretend the save succeeded, and return the failure so the sheet
    // can show its line (the DB-first "a failed write surfaces" contract).
    const ok = await saveUserContacts(chatRoomId, userId, next);
    if (!ok) return { error: 'save_failed' };

    localStorage.setItem(`user_contacts_${chatRoomId}`, JSON.stringify(next));
    setConnections((prev) => ({
      ...prev,
      [chatRoomId]: { ...prev[chatRoomId], userContacts: next },
    }));
  }, []);

  // Per-connection nickname (DB-first) — the owner's short "how I know them"
  // descriptor, stored on connection_views.nickname. Same pattern as user_contacts:
  // DB is the source of truth, localStorage is a cache for fast first paint.
  const loadNickname = useCallback(async (chatRoomId) => {
    if (!chatRoomId) return;
    const userId = getSenderId();
    if (!userId) return;

    // Paint the cached value immediately, then refresh from the DB (source of truth).
    const cached = localStorage.getItem(`nickname_${chatRoomId}`);
    if (cached) {
      setConnections((prev) => ({
        ...prev,
        [chatRoomId]: { ...prev[chatRoomId], nickname: cached },
      }));
    }

    const nickname = await getNickname(chatRoomId, userId);
    localStorage.setItem(`nickname_${chatRoomId}`, nickname);
    setConnections((prev) => ({
      ...prev,
      [chatRoomId]: { ...prev[chatRoomId], nickname },
    }));
  }, []);

  const updateNickname = useCallback(async (chatRoomId, nickname) => {
    if (!chatRoomId) return false;
    const userId = getSenderId();
    if (!userId) return false;

    const next = (nickname || '').trim();

    // DB first (awaited). If the write fails, don't update cache/state — never let
    // stale data pretend the save succeeded.
    const ok = await saveNickname(chatRoomId, userId, next);
    if (!ok) return false;

    localStorage.setItem(`nickname_${chatRoomId}`, next);
    setConnections((prev) => ({
      ...prev,
      [chatRoomId]: { ...prev[chatRoomId], nickname: next },
    }));
    return true;
  }, []);

  // Context value with all methods and state
  const value = {
    connections,
    currentChatRoomId,
    setCurrentChatRoomId,
    getConnectionData,
    loadConnectionData,
    updateProfileData,
    updateAnonData,
    updateDisplayName,
    updateManualContactData,
    updateConnectionMetadata,
    updateNotes,
    updateChatMessages,
    addChatMessage,
    updateContactSharingStatus,
    updateContactSavedStatus,
    updateConnectionGroup,
    loadUserContacts,
    updateUserContacts,
    loadNickname,
    updateNickname,
    clearConnectionData,
    // User preference flags
    userPreferences,
    setHasSignedUpForQR,
    setWaitlistHeld,
    dismissBanner,
  };

  return (
    <ConnectionStateContext.Provider value={value}>{children}</ConnectionStateContext.Provider>
  );
};

// Custom hook to use the connection context
export const useConnectionContext = () => {
  const context = useContext(ConnectionStateContext);
  if (!context) {
    throw new Error('useConnectionContext must be used within ConnectionStateProvider');
  }
  return context;
};

// Hook to get data for a specific chat room
export const useConnectionData = (chatRoomId) => {
  const { getConnectionData, loadConnectionData, loadUserContacts, loadNickname } =
    useConnectionContext();

  // Load from DB on mount — catches any data sent while offline.
  // Real-time delivery comes via WebSocket full payloads → global handler.
  useEffect(() => {
    if (!chatRoomId) return;
    loadConnectionData(chatRoomId);
    // DB-first fields — independent of the legacy load path above.
    loadUserContacts(chatRoomId);
    loadNickname(chatRoomId);
  }, [chatRoomId, loadConnectionData, loadUserContacts, loadNickname]);

  // Refresh data when app returns to foreground (visibility change)
  // This catches messages sent while app was backgrounded
  // Note: visibilitychange works for tab switches but may not fire reliably
  // for iOS PWA app switches - that's a known iOS limitation
  useEffect(() => {
    if (!chatRoomId) return;

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        console.log('[useConnectionData] App became visible, refreshing data for:', chatRoomId);
        loadConnectionData(chatRoomId);
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [chatRoomId, loadConnectionData]);

  return getConnectionData(chatRoomId);
};
